// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: ClickUp statuses that count as done for this person in Konvo, e.g.
// "to be invoiced": finished work that only leaves ClickUp's Active group once
// it is invoiced. The server applies the choice to everything it sends (tasks
// arrive typed 'done'), so after a change every task view is refetched.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import KonvoWorkspace from '@app/features/konvo_tasks/state/KonvoWorkspace';
import {reloadNativeWidgets} from '@app/features/platform/native_mobile/NativeWidgets';
import {http} from '@app/features/platform/transport/RestTransport';
import {makeAutoObservable, runInAction} from 'mobx';

export interface DoneStatusOption {
	status: string;
	color: string | null;
	/** Open tasks in this status in ClickUp across the lists you can see. */
	open_count: number;
	/** Of those, assigned to you. */
	mine_count: number;
}

interface DoneStatusesResponse {
	connected?: boolean;
	done_statuses?: Array<string>;
	options?: Array<DoneStatusOption>;
}

export function doneStatusKey(name: string): string {
	return name.trim().toLowerCase();
}

class KonvoClickUpDoneStatusesStore {
	selected: Array<string> = [];
	options: Array<DoneStatusOption> = [];
	connected: boolean | null = null;
	loading = false;
	saving = false;
	error = false;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	isSelected(status: string): boolean {
		return this.selected.includes(doneStatusKey(status));
	}

	async load(): Promise<void> {
		this.loading = true;
		try {
			const response = await http.get<DoneStatusesResponse>(Endpoints.KONVO_CLICKUP_DONE_STATUSES);
			const body = response.body ?? {};
			runInAction(() => {
				this.connected = body.connected !== false;
				this.selected = body.done_statuses ?? [];
				this.options = body.options ?? [];
				this.error = false;
			});
		} catch {
			runInAction(() => {
				this.error = true;
			});
		} finally {
			runInAction(() => {
				this.loading = false;
			});
		}
	}

	/** Optimistic; rolls back if the server refuses, then refreshes every task view. */
	async toggle(status: string, on: boolean): Promise<boolean> {
		const key = doneStatusKey(status);
		const previous = this.selected;
		const next = on ? [...new Set([...previous, key])] : previous.filter((entry) => entry !== key);
		this.selected = next;
		this.saving = true;
		try {
			const response = await http.put<DoneStatusesResponse>(Endpoints.KONVO_CLICKUP_DONE_STATUSES, {
				body: {done_statuses: next},
			});
			runInAction(() => {
				this.selected = response.body?.done_statuses ?? next;
			});
			KonvoWorkspace.refreshAll();
			reloadNativeWidgets();
			return true;
		} catch {
			runInAction(() => {
				this.selected = previous;
			});
			return false;
		} finally {
			runInAction(() => {
				this.saving = false;
			});
		}
	}
}

export default new KonvoClickUpDoneStatusesStore();
